import React, { useState } from 'react';
import { respondFamilyChangeConsent, type MyFamilyChangeRequest } from './familyChangeClient';

interface FamilyRequestsListProps {
  requests: MyFamilyChangeRequest[];
  /** Called after a consent response succeeds, so the caller can refresh the list. */
  onConsentResolved: () => void;
}

/** Status label (R6, R10): Waiting for the committee / Added / Removed / Approved / Not approved: <reason>. */
function statusLabel(r: MyFamilyChangeRequest): string {
  if (r.status === 'PENDING') return 'Waiting for the committee';
  if (r.status === 'REJECTED') return `Not approved: ${r.decisionReason || 'no reason given'}`;
  // APPROVED
  if (r.kind === 'REMOVE_MEMBER') return 'Removed';
  if (r.kind === 'PORTAL_ACCESS') return 'Approved';
  return 'Added';
}

function requestTitle(r: MyFamilyChangeRequest): string {
  if (r.kind === 'NEW_PERSON' && r.personFirstName) {
    return `Add ${r.personFirstName} ${r.personLastName || ''}`.trim();
  }
  if (r.kind === 'EXISTING_MEMBER') return 'Add an existing member';
  if (r.kind === 'REMOVE_MEMBER') return 'Request removal';
  if (r.kind === 'PORTAL_ACCESS') {
    const name = r.person?.firstName ? `${r.person.firstName} ${r.person.lastName || ''}`.trim() : null;
    return name ? `Login request for ${name}` : 'Login request';
  }
  return 'Family change request';
}

const statusBadgeCls: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
};

/**
 * "Family requests" status list (R6.6/R10.7, design §7.2 point 6) and
 * consent cards ("<first name> would like to add you to their family",
 * Accept / Decline) for requests awaiting the caller's consent.
 */
const FamilyRequestsList: React.FC<FamilyRequestsListProps> = ({ requests, onConsentResolved }) => {
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);

  if (requests.length === 0) return null;

  const consentRequests = requests.filter((r) => r.awaitingMyConsent);
  const ownRequests = requests.filter((r) => !r.awaitingMyConsent);

  const handleRespond = async (requestId: string, accept: boolean) => {
    setRespondingId(requestId);
    setConsentError(null);
    try {
      const result = await respondFamilyChangeConsent(requestId, accept);
      if (result.success) {
        onConsentResolved();
      } else {
        setConsentError(result.message || 'Could not record your response. Please try again.');
      }
    } catch (err: any) {
      setConsentError(err?.message || 'Could not record your response. Please try again.');
    } finally {
      setRespondingId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Family requests</h3>

      {consentError && (
        <p role="alert" className="mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
          {consentError}
        </p>
      )}

      {consentRequests.length > 0 && (
        <div className="space-y-3 mb-4">
          {consentRequests.map((r) => (
            <div
              key={r.id}
              className="rounded-lg border border-blue-200 bg-blue-50 p-4"
              data-testid="family-consent-card"
            >
              <p className="text-sm font-medium text-blue-900">
                {r.requesterFirstName || 'Someone'} would like to add you to their family
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => handleRespond(r.id, true)}
                  disabled={respondingId === r.id}
                  className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Accept
                </button>
                <button
                  type="button"
                  onClick={() => handleRespond(r.id, false)}
                  disabled={respondingId === r.id}
                  className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {ownRequests.length > 0 && (
        <ul className="space-y-3">
          {ownRequests.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3"
              data-testid="family-request-row"
            >
              <div>
                <p className="text-sm font-medium text-gray-900">{requestTitle(r)}</p>
                {r.amountDueCents > 0 && (
                  <p className="text-xs text-gray-500">
                    ${(r.amountDueCents / 100).toFixed(2)}
                    {r.paymentReceived ? ' paid' : ' owing'}
                  </p>
                )}
              </div>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                  statusBadgeCls[r.status] || 'bg-gray-100 text-gray-800'
                }`}
              >
                {statusLabel(r)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FamilyRequestsList;
